import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, MessageCircle, Camera, Mountain, ArrowRight, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '919871155963'; // central GoHimalayan WA

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const STAGE_LABEL = {
  pending_review: { label: 'In review', color: 'bg-amber-100 text-amber-800' },
  in_curation: { label: 'Being curated', color: 'bg-blue-100 text-blue-800' },
  ai_drafted: { label: 'Draft ready', color: 'bg-violet-100 text-violet-800' },
  active: { label: 'Live!', color: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Not accepted', color: 'bg-rose-100 text-rose-800' },
};

export default function ApplyThanks() {
  const { id } = useParams();
  const [inquiry, setInquiry] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/apply/inquiry/${encodeURIComponent(id)}`);
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) {
          setError(data.detail || 'Could not load your application.');
        } else {
          setInquiry(data.inquiry);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Network error');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg border border-stone-200 p-8 max-w-md text-center" data-testid="thanks-error">
          <AlertTriangle className="mx-auto mb-3 text-amber-600" size={32} strokeWidth={1.4} />
          <p className="text-sm text-stone-700 mb-2">We couldn't find your application.</p>
          <p className="text-xs text-stone-500 mb-4">{error}</p>
          <Link to="/apply" className="text-sm text-stone-900 underline">← Back to apply</Link>
        </div>
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center" data-testid="thanks-loading">
        <p className="text-sm text-stone-500">Loading your application…</p>
      </div>
    );
  }

  const photoCount = inquiry.image_count || 0;
  const stage = STAGE_LABEL[inquiry.curation_status] || STAGE_LABEL.pending_review;
  const lowPhotos = photoCount < 5;
  const noEmail = !inquiry.host_email;
  const inv = inquiry.inventory || {};
  const invEntries = Object.entries(inv).filter(([, v]) => v && v > 0);

  const photoMsg = `Hi GoHimalayan — sending photos for my application: ${inquiry.display_name} (${inquiry.gohimalayan_id})`;
  const confirmMsg = `Hi GoHimalayan — confirming my application: ${inquiry.display_name} (${inquiry.gohimalayan_id})`;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero confirmation */}
      <div className="bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <CheckCircle2 className="mx-auto mb-4 text-amber-300" size={42} strokeWidth={1.4} />
          <div className="flex items-center justify-center gap-2 mb-3">
            <Mountain size={16} className="text-amber-300" strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.4em] uppercase text-amber-300 font-semibold">Application Received</span>
          </div>
          <h1 className="text-3xl sm:text-4xl mb-3 leading-tight" style={pf}>Thank you, {inquiry.host_name || 'host'}.</h1>
          <p className="text-sm text-stone-300 max-w-lg mx-auto leading-relaxed">
            We've saved your details for <em className="text-amber-200 not-italic font-medium">{inquiry.display_name}</em>.
            Our editorial team reviews every submission within 48 hours.
          </p>
          <div className="mt-5 inline-flex items-center gap-2">
            <span className="text-[10px] tracking-wider uppercase text-stone-400">Reference</span>
            <span className="font-mono text-xs text-white bg-white/10 px-2 py-1 rounded" data-testid="thanks-gh-id">{inquiry.gohimalayan_id}</span>
            <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${stage.color}`}>{stage.label}</span>
          </div>
        </div>
      </div>

      {/* Action recap */}
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {/* What we captured */}
        <section className="bg-white rounded-lg border border-stone-200 p-6" data-testid="thanks-captured">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-4 font-semibold">What we captured</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row label="Property" value={inquiry.display_name} />
            <Row label="Host" value={inquiry.host_name || '—'} />
            <Row label="WhatsApp" value={inquiry.whatsapp_number || '—'} />
            <Row label="Email" value={inquiry.host_email || <span className="text-amber-700">not provided</span>} />
            <Row label="Valley" value={inquiry.valley || inquiry.valley_slug} />
            <Row label="Property type" value={inquiry.property_type} />
            {inquiry.altitude_meters && <Row label="Altitude" value={`${inquiry.altitude_meters} m`} />}
            <Row label="Sleeps (max)" value={inquiry.sleeps_max || '—'} />
            {inquiry.starting_price_inr && (
              <Row
                label="Starting price"
                value={
                  <span>
                    ₹{Number(inquiry.starting_price_inr).toLocaleString('en-IN')}
                    <span className="text-xs text-stone-500"> / night
                      {inquiry.price_unit === 'per_person' ? ' / person' : inquiry.price_unit === 'whole_property' ? ' (whole property)' : ' / room'}
                      {inquiry.includes_breakfast ? ' · incl. breakfast' : ''}
                    </span>
                  </span>
                }
              />
            )}
          </dl>

          {invEntries.length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-100">
              <p className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-2 font-semibold">Inventory</p>
              <div className="flex flex-wrap gap-2">
                {invEntries.map(([k, v]) => (
                  <span key={k} className="text-xs bg-stone-100 text-stone-700 px-2.5 py-1 rounded-full">
                    <b>{v}</b> {k.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {inquiry.tagline && (
            <div className="mt-4 pt-4 border-t border-stone-100">
              <p className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1 font-semibold">Tagline</p>
              <p className="text-sm font-serif text-stone-800 italic">"{inquiry.tagline}"</p>
            </div>
          )}
        </section>

        {/* Photos summary */}
        <section className="bg-white rounded-lg border border-stone-200 p-6" data-testid="thanks-photos">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-stone-500 font-semibold">Photos received</h2>
            <span className={`text-xs font-semibold ${lowPhotos ? 'text-amber-700' : 'text-emerald-700'}`}>
              {photoCount} photo{photoCount !== 1 ? 's' : ''}
            </span>
          </div>

          {photoCount > 0 ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
              {inquiry.images.slice(0, 12).map((img, i) => (
                <div key={i} className="aspect-square rounded overflow-hidden bg-stone-100">
                  <img src={img.url.startsWith('http') ? img.url : `${API}${img.url}`} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              {photoCount > 12 && (
                <div className="aspect-square rounded bg-stone-100 flex items-center justify-center text-[10px] text-stone-500">
                  +{photoCount - 12} more
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-amber-700 leading-relaxed">
              No photos attached yet. Tap the green button below to send photos via WhatsApp — we'll attach them to your application.
            </p>
          )}

          {lowPhotos && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 leading-relaxed">
              <b>We recommend at least 8-10 photos</b> for a strong editorial listing — exterior, rooms, common areas, and surroundings.
              The more we have, the better your storytelling page.
            </div>
          )}
        </section>

        {/* What happens next */}
        <section className="bg-white rounded-lg border border-stone-200 p-6" data-testid="thanks-next-steps">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-3 font-semibold">What happens next</h2>
          <ol className="space-y-3 text-sm text-stone-700">
            <Step n="1" title="Editorial review (24-48h)" body="Our team audits your photos and details for the editorial bar." />
            <Step n="2" title="AI-assisted draft" body="We compose a tagline, storyline, and curated gallery — then send it to you for review." />
            <Step n="3" title="WhatsApp confirmation" body={`We'll message you on ${inquiry.whatsapp_number || 'WhatsApp'} once your page is ready. You'll be able to approve or request edits.`} />
            <Step n="4" title="Goes live" body="Your page launches on GoHimalayan. Bookings come direct to your WhatsApp — zero commission, ever." />
          </ol>
        </section>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3" data-testid="thanks-ctas">
          {photoCount < 8 && (
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(photoMsg)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-5 py-3 bg-emerald-600 text-white rounded font-medium hover:bg-emerald-700 flex items-center justify-center gap-2"
              data-testid="thanks-send-photos-btn"
            >
              <Camera size={16} /> Send more photos via WhatsApp
            </a>
          )}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(confirmMsg)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-5 py-3 bg-stone-900 text-white rounded font-medium hover:bg-stone-800 flex items-center justify-center gap-2"
            data-testid="thanks-confirm-wa-btn"
          >
            <MessageCircle size={16} /> Confirm on WhatsApp
          </a>
        </div>

        {noEmail && (
          <p className="text-[11px] text-stone-500 text-center">
            Tip: tap "Confirm on WhatsApp" so we have a contactable channel — you didn't add an email.
          </p>
        )}

        <div className="text-center pt-2">
          <Link to="/discovered" className="text-xs text-stone-600 underline hover:text-stone-900 inline-flex items-center gap-1" data-testid="thanks-browse-link">
            Browse our curated stays <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div>
    <dt className="text-[10px] tracking-[0.2em] uppercase text-stone-400 font-semibold mb-0.5">{label}</dt>
    <dd className="text-stone-900">{value}</dd>
  </div>
);

const Step = ({ n, title, body }) => (
  <li className="flex gap-3">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-900 text-white text-xs font-semibold flex items-center justify-center">{n}</span>
    <div>
      <p className="font-medium text-stone-900">{title}</p>
      <p className="text-xs text-stone-600 mt-0.5 leading-relaxed">{body}</p>
    </div>
  </li>
);
